import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { AuthConfigurationNotice, SignInPrompt } from '@/auth/AuthNotices'
import { completeLogin, AuthError } from '@/auth/oidc'
import { useAuth } from '@/auth/useAuth'
import { FullPageLoader } from '@/components/states'
import { oidcConfig } from '@/lib/env'

/**
 * Landing point for the Keycloak redirect (`VITE_KEYCLOAK_REDIRECT_URI`).
 * Exchanges the authorization code, then bounces to wherever the user was going.
 */
export function CallbackPage() {
  const navigate = useNavigate()
  const { acceptTokens, login } = useAuth()
  const [error, setError] = useState<string | undefined>(undefined)
  const started = useRef(false)

  const config = oidcConfig()

  useEffect(() => {
    if (config === undefined || started.current) return
    started.current = true

    void (async () => {
      try {
        const { tokens, returnTo } = await completeLogin(config, window.location.search)
        acceptTokens(tokens)
        // Drop the code/state from history so a refresh cannot replay it.
        navigate(returnTo, { replace: true })
      } catch (cause) {
        setError(
          cause instanceof AuthError
            ? cause.message
            : 'The sign-in response could not be processed.',
        )
      }
    })()
  }, [acceptTokens, config, navigate])

  if (config === undefined) return <AuthConfigurationNotice />

  if (error !== undefined) {
    return (
      <SignInPrompt
        message={error}
        onRetry={() => {
          login('/app')
        }}
      />
    )
  }

  return <FullPageLoader label="Completing sign-in…" />
}
